import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { t } from 'i18next'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { styled } from 'styled-components'
import { SectionLoadingState } from '../../../../components'
import { NO_DATA_PLACEHOLDER } from '../../../../constants/constants'
import { useAlgorithmRunDetails } from '../../../../hooks'
import {
  FlowCytometryNodeResult,
  FlowCytometryReviewAnalysisRunResult,
  ReviewAnalysisGateReviewRow,
} from '../../../../store/reducers/workspaceReducer'
import { SmartFunctionListPanelLayout } from '../../components/SmartFunctionListPanelLayout'

const SectionLoadingStateContainer = styled.div`
  display: flex;
  height: calc(100vh - 224px);
`

const StyledTableWrapper = styled.div`
  .ant-table .ant-table-thead .ant-table-cell-fix-left {
    background-color: var(--color-bg-base);
  }
`

interface ExtendedReviewAnalysisGateReviewRow extends ReviewAnalysisGateReviewRow {
  key: string
}

const FlowCytometryReviewAnalysisGateReviewPanel = () => {
  const { workspaceId, algorithmRunId } = useParams()

  const { loading, selectedAlgorithmRun } = useAlgorithmRunDetails({
    workspaceId,
    algorithmRunId,
  })

  const nodeResults = useMemo(
    () =>
      selectedAlgorithmRun?.nodeResults
        ? ((selectedAlgorithmRun?.nodeResults as FlowCytometryNodeResult)
            ?.REVIEW_ANALYSIS as FlowCytometryReviewAnalysisRunResult)
        : undefined,
    [selectedAlgorithmRun],
  )

  const formattedData: ExtendedReviewAnalysisGateReviewRow[] = useMemo(
    () =>
      nodeResults && nodeResults.gateReview
        ? nodeResults.gateReview.map(
            (gate: ReviewAnalysisGateReviewRow) =>
              ({
                ...gate,
                key: gate.patientId,
              }) as ExtendedReviewAnalysisGateReviewRow,
          )
        : [],
    [nodeResults],
  )

  // const formattedData: ExtendedReviewAnalysisGateReviewRow[] = [
  //   {
  //     key: '1',
  //     patientId: 'A01-002',
  //     filename: 'ADO0007P0007P_CTRL_A_F10_006.fcs',
  //     visit: 'C1D9 24h after SoI',
  //     exportTime: '28-FEB-2025-21:00:30',
  //     gateName: 'CD45+SSClow',
  //     parentPopulation: 'CD45+',
  //     flagMessage:
  //       'Polygon bounding box differs on both X and Y axes by more than 5.0%.',
  //     standardFileRef: 'ADO0001M0005P_FULL_A_B04_001.fcs',
  //     severity: 'CRITICAL',
  //     deviationPercent: 50,
  //   },
  // ]

  const columns: ColumnsType<ReviewAnalysisGateReviewRow> = useMemo(
    () => [
      {
        title: t(
          'smartFunctionDetailsPage.cellCartaFlowCytometryReviewAnalysis.tableTitles.patientId',
        ),
        dataIndex: 'patientId',
        key: 'patientId',
        width: 100,
        fixed: 'left',
        render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
      },
      {
        title: t(
          'smartFunctionDetailsPage.cellCartaFlowCytometryReviewAnalysis.tableTitles.filename',
        ),
        dataIndex: 'filename',
        key: 'filename',
        width: 250,
        render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
      },
      {
        title: t(
          'smartFunctionDetailsPage.cellCartaFlowCytometryReviewAnalysis.tableTitles.visit',
        ),
        dataIndex: 'visit',
        key: 'visit',
        width: 100,
        render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
      },
      {
        title: t(
          'smartFunctionDetailsPage.cellCartaFlowCytometryReviewAnalysis.tableTitles.gateName',
        ),
        dataIndex: 'gateName',
        key: 'gateName',
        width: 200,
        render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
      },
      {
        title: t(
          'smartFunctionDetailsPage.cellCartaFlowCytometryReviewAnalysis.tableTitles.parentPopulation',
        ),
        dataIndex: 'parentPopulation',
        key: 'parentPopulation',
        width: 100,
        render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
      },
      {
        title: t(
          'smartFunctionDetailsPage.cellCartaFlowCytometryReviewAnalysis.tableTitles.deviationPercent',
        ),
        dataIndex: 'deviationPercent',
        key: 'deviationPercent',
        width: 100,
        render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
      },
      {
        title: t(
          'smartFunctionDetailsPage.cellCartaFlowCytometryReviewAnalysis.tableTitles.severity',
        ),
        dataIndex: 'severity',
        key: 'severity',
        width: 100,
        render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
      },
      {
        title: t(
          'smartFunctionDetailsPage.cellCartaFlowCytometryReviewAnalysis.tableTitles.flagMessage',
        ),
        dataIndex: 'flagMessage',
        key: 'flagMessage',
        width: 300,
        render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
      },
      {
        title: t(
          'smartFunctionDetailsPage.cellCartaFlowCytometryReviewAnalysis.tableTitles.standardFileRef',
        ),
        dataIndex: 'standardFileRef',
        key: 'standardFileRef',
        width: 100,
        render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
      },
    ],
    [],
  )

  return loading ? (
    <SectionLoadingStateContainer>
      <SectionLoadingState />
    </SectionLoadingStateContainer>
  ) : (
    <SmartFunctionListPanelLayout>
      <StyledTableWrapper>
        <Table
          columns={columns}
          dataSource={formattedData}
          loading={loading}
          rowKey="key"
          pagination={false}
          scroll={{ x: 'max-content', y: 'calc(100vh - 190px)' }}
          rowClassName="page-list-table-row"
        />
      </StyledTableWrapper>
    </SmartFunctionListPanelLayout>
  )
}

export default FlowCytometryReviewAnalysisGateReviewPanel
